/**
 * Purpose: Utility functions for date range calculations and formatting in the Reservations page.
 * 
 * High-level Workflow:
 * 1. getDateRangeFromReportingPeriod: Converts Reporting Period context dates to YYYY-MM-DD format for API queries
 * 2. formatReportingPeriodRange: Formats Reporting Period dates for display in the UI header
 * 
 * Assumptions & Invariants:
 * - Input dates from ReportingPeriodContext are valid Date objects
 * - Date formatting uses local timezone (no timezone shifts for date-only strings)
 * - API expects YYYY-MM-DD format strings
 * - UI display uses formatDateOnlyET for consistent ET timezone formatting
 * 
 * Dependencies:
 * Internal: utils/dateTimeET.js (formatDateOnlyET)
 * External: None
 * 
 * Version: 1.0.0
 * Created: 2026-01-26
 * Change Log:
 * - 2026-01-26: Initial extraction from ReservationsPage.jsx
 */

import { formatDateOnlyET } from './dateTimeET.js';

/**
 * Convert reporting period to date range (YYYY-MM-DD format)
 * 
 * CRITICAL: Use Reporting Period dates for API query (not wide range)
 * This ensures KPIs, table contents, and search scope all align with Reporting Period
 * 
 * @param {Date|null} rangeStart - Start date from ReportingPeriodContext
 * @param {Date|null} rangeEnd - End date from ReportingPeriodContext
 * @param {boolean} debug - Optional debug flag for logging
 * @returns {Object} - Object with startDate and endDate in YYYY-MM-DD format
 */
export function getDateRangeFromReportingPeriod(rangeStart, rangeEnd, debug = false) {
  if (!rangeStart || !rangeEnd) {
    // Fallback to current month if Reporting Period not set
    // Use getFullYear/getMonth/getDate (not toISOString()) so date is correct in all timezones
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const startStr = `${currentMonthStart.getFullYear()}-${String(currentMonthStart.getMonth() + 1).padStart(2, '0')}-${String(currentMonthStart.getDate()).padStart(2, '0')}`;
    const endStr = `${currentMonthEnd.getFullYear()}-${String(currentMonthEnd.getMonth() + 1).padStart(2, '0')}-${String(currentMonthEnd.getDate()).padStart(2, '0')}`;
    return {
      startDate: startStr,
      endDate: endStr,
    };
  }
  
  // Use Reporting Period dates directly for API query
  const startDate = new Date(rangeStart);
  const endDate = new Date(rangeEnd);
  
  // Format as YYYY-MM-DD
  const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
  const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
  
  if (debug) {
    console.log('[ReservationsPage] getDateRangeFromReportingPeriod:', {
      reportingPeriod: {
        start: startDateStr,
        end: endDateStr,
      },
      note: 'API uses Reporting Period dates directly',
    });
  }
  
  return {
    startDate: startDateStr,
    endDate: endDateStr,
  };
}

/**
 * Format Reporting Period date range for header display
 * 
 * Formats dates as "MMM D, YYYY" (e.g., "Jul 1, 2025") using ET timezone formatting
 * 
 * @param {Date|null} rangeStart - Start date from ReportingPeriodContext
 * @param {Date|null} rangeEnd - End date from ReportingPeriodContext
 * @returns {string|null} - Formatted date range string or null if dates not available
 */
export function formatReportingPeriodRange(rangeStart, rangeEnd) {
  if (!rangeStart || !rangeEnd) {
    return null;
  }
  
  // Format dates as "MMM D, YYYY" (e.g., "Jul 1, 2025")
  const startFormatted = formatDateOnlyET(
    `${rangeStart.getFullYear()}-${String(rangeStart.getMonth() + 1).padStart(2, '0')}-${String(rangeStart.getDate()).padStart(2, '0')}`,
    { short: true }
  );
  const endFormatted = formatDateOnlyET(
    `${rangeEnd.getFullYear()}-${String(rangeEnd.getMonth() + 1).padStart(2, '0')}-${String(rangeEnd.getDate()).padStart(2, '0')}`,
    { short: true }
  );
  
  return `${startFormatted} – ${endFormatted}`;
}
